import {
    Avatar,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@mui/material';
import React, { Fragment, useCallback, useEffect, useReducer } from 'react';
import { mapDispatchToProps, mapStateToProps } from './AccountSecurity.container';
import { verifyPhoneSchema, verifyTotpSchema } from './AccountSecurity.schema';

import AuthenticatorCode from '/b2b/common/components/AuthenticatorCode';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { DEFAULT_MFA_OPTIONS } from '/b2b/authentication/state/amplify/Amplify.constants';
import { FormikCheckbox } from '/b2b/common/components/FormControls/Checkbox';
import FormikDialog from '/b2b/common/components/FormikDialog';
import { FormikPhoneNumber } from '/b2b/common/components/FormControls/PhoneNumber';
import { FormikTextField } from '/b2b/common/components/FormControls/TextField';
import Loader from '/b2b/common/components/Loader';
import Locale from '/b2b/common/components/Locale';
import LocalizedButton from '/b2b/common/components/LocalizedButton';
import Portlet from '/b2b/common/components/Portlet';
import PortletContent from '/b2b/common/components/PortletContent';
import PropTypes from 'prop-types';
import ResponsiveDialog from '/b2b/common/components/ResponsiveDialog';
import SMSIcon from '@mui/icons-material/Sms';
import SubmitButton from '/b2b/common/components/SubmitButton';
import TOTPIcon from '@mui/icons-material/ScreenLockPortrait';
import { connect } from 'react-redux';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';

const MFA_OPTIONS = {
    SMS: {
        icon: SMSIcon,
        primary: 'views.Account.components.AccountSecurity.sms',
        secondary: 'views.Account.components.AccountSecurity.smsRatesApply',
        value: 'SMS',
    },
    TOTP: {
        icon: TOTPIcon,
        primary: 'views.Account.components.AccountSecurity.authenticatorApp',
        secondary: 'views.Account.components.AccountSecurity.authenticatorAppExamples',
        value: 'TOTP',
    },
};

const initialState = {
    phoneDisabled: false,
    removeType: null,
    setupType: null,
    smsSent: false,
    sendSms: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'ADD_MFA':
            return { ...state, setupType: action.payload };
        case 'REMOVE_MFA':
            return { ...state, removeType: action.payload };
        case 'SMS_SENT':
            return { ...state, phoneDisabled: true, smsSent: true, sendSms: false };
        case 'CHANGE_PHONE':
            return { ...state, phoneDisabled: false, smsSent: false, sendSms: false };
        case 'CLOSE':
            return { ...state, removeType: null, setupType: null, ...action.payload };
        default:
            return state;
    }
};

function MfaOption({ color = 'primary', label, option, onClick, verified }) {
    const { icon: IconComponent, primary, secondary, value, ...remain } = MFA_OPTIONS[option];
    const handleClick = useBoundCallback(onClick, [value, verified]);

    return (
        <ListItem key={value}>
            <ListItemAvatar>
                <Avatar>
                    <IconComponent />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={<Locale path={primary} />}
                secondary={<Locale path={secondary} />}
                {...remain}
            />
            <ListItemSecondaryAction>
                <LocalizedButton
                    edge="end"
                    ariaLabel={label}
                    onClick={handleClick}
                    color={color}
                    variant="contained"
                >
                    <Locale path={label} />
                </LocalizedButton>
            </ListItemSecondaryAction>
        </ListItem>
    );
}

MfaOption.propTypes = {
    color: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    option: PropTypes.oneOf(DEFAULT_MFA_OPTIONS).isRequired,
    verified: PropTypes.bool,
};

// eslint-disable-next-line no-use-before-define
export default connect(mapStateToProps, mapDispatchToProps)(AccountSecurity);

export function AccountSecurity(props) {
    const {
        getMfaOptions,
        mfaOptions,
        additionalMfaOptions,
        isLoading,
        isPhoneVerified,
        /** @type {import('node_modules/@aws-amplify/auth/dist/esm/types').AuthCodeDeliveryDetails<'phone_number'>|null} */
        smsCodeDeliveryDetails,
        resendPhoneVerificationCode,
        phone,
        addMFA,
        removeMFA,
        updatePhone,
    } = props;

    const [state, dispatch] = useReducer(reducer, initialState, () => {
        return {
            ...initialState,
            phoneDisabled: (phone && isPhoneVerified) || smsCodeDeliveryDetails,
            sendSms: phone && !isPhoneVerified && !smsCodeDeliveryDetails,
        };
    });
    const { setupType, removeType, sendSms, smsSent, phoneDisabled } = state;
    const [resendingCode, setResendingCode] = React.useState(false);

    const handleConfigureMfaType = useCallback(mfaType => {
        dispatch({ type: 'ADD_MFA', payload: mfaType });
    }, []);

    const handleRemoveMfaType = useCallback((mfaType, verified) => {
        if (verified) {
            dispatch({ type: 'REMOVE_MFA', payload: mfaType });
        } else {
            dispatch({ type: 'ADD_MFA', payload: mfaType });
        }
    }, []);

    const handleClose = useBoundCallback(
        (phone, isPhoneVerified, smsCodeDeliveryDetails) => {
            dispatch({
                type: 'CLOSE',
                payload: { phoneDisabled: (phone && isPhoneVerified) || smsCodeDeliveryDetails },
            });
        },
        [phone, isPhoneVerified, smsCodeDeliveryDetails]
    );
    const handleResendSms = useBoundCallback(() => {
        setResendingCode(true);
        return resendPhoneVerificationCode()
            .then(() => {
                dispatch({ type: 'SMS_SENT' });
                setResendingCode(false);
            })
            .catch(() => {
                setResendingCode(false);
            });
    }, []);
    const handleUpdatePhone = useBoundCallback(params => {
        return updatePhone(params).then(
            (/** @type {import('aws-amplify/auth').UpdateUserAttributesOutput} */ response) => {
                // If the phone number needs verification, the code should already be sent. We can check the status
                if (
                    response.phone_number &&
                    response.phone_number.nextStep.updateAttributeStep ===
                        'CONFIRM_ATTRIBUTE_WITH_CODE'
                ) {
                    dispatch({ type: 'SMS_SENT' });
                    return params;
                } else {
                    return addMFA({
                        ...params,
                        mfaType: 'SMS',
                        verified: true,
                    }).then(() => {
                        dispatch({
                            type: 'CLOSE',
                            payload: { smsSent: true },
                        });
                        return params;
                    });
                }
            }
        );
    }, []);
    const handleChangePhone = useCallback(() => {
        dispatch({ type: 'CHANGE_PHONE' });
    }, []);
    const handleSetupConfirm = useBoundCallback(
        (mfaType, params) => {
            return addMFA({ ...params, mfaType }).then(() => {
                dispatch({
                    type: 'CLOSE',
                    payload: mfaType === 'SMS' ? { smsSent: true } : undefined,
                });
                return params;
            });
        },
        [setupType]
    );

    const handleRemoveTypeConfirm = useBoundCallback(
        (mfaType, params) => {
            return removeMFA({ mfaType, ...params }).then(() => {
                dispatch({
                    type: 'CLOSE',
                });
            });
        },
        [removeType]
    );

    useEffect(() => {
        if (setupType === 'SMS' && sendSms && !smsSent) {
            handleResendSms();
        }
    }, [sendSms, setupType, smsSent]);

    useEffect(() => {
        if (!isLoading && mfaOptions.length === 0) {
            getMfaOptions();
        }
    }, []);

    return (
        <Portlet>
            <PortletContent>
                <Typography my={2} variant="h2">
                    <Locale path="views.Account.components.AccountSecurity.heading" />
                </Typography>
                {mfaOptions.length > 0 && (
                    <Fragment>
                        <Typography variant="h4" gutterBottom>
                            <Locale path="views.Account.components.AccountSecurity.configuredOptions" />
                        </Typography>
                        <Divider />
                        <List>
                            {mfaOptions.map(option => {
                                const verified = option === 'SMS' ? isPhoneVerified : true;
                                return (
                                    <MfaOption
                                        key={option}
                                        label={
                                            verified
                                                ? 'views.Account.components.AccountSecurity.remove'
                                                : 'common.verify'
                                        }
                                        onClick={handleRemoveMfaType}
                                        option={option}
                                        color={verified ? 'error' : 'warning'}
                                        verified={verified}
                                    />
                                );
                            })}
                        </List>
                    </Fragment>
                )}
                {additionalMfaOptions.length > 0 && (
                    <Fragment>
                        <Typography variant="h4" gutterBottom>
                            <Locale
                                path={[
                                    'views.Account.components.AccountSecurity.additional',
                                    'views.Account.components.AccountSecurity.MFAOptions',
                                ]}
                            />
                        </Typography>
                        <Divider />
                        <List>
                            {additionalMfaOptions.map(option => {
                                const verified = option === 'SMS' ? isPhoneVerified : false;
                                return (
                                    <MfaOption
                                        key={option}
                                        label="views.Account.components.AccountSecurity.configure"
                                        onClick={handleConfigureMfaType}
                                        option={option}
                                        verified={verified}
                                    />
                                );
                            })}
                        </List>
                    </Fragment>
                )}
                <Loader loading={isLoading} />
            </PortletContent>
            <FormikDialog
                title={'views.Account.components.AccountSecurity.dialogs.setupAuthenticator'}
                onClose={handleClose}
                onConfirm={handleSetupConfirm}
                open={setupType === 'TOTP'}
                confirmText="auth.amplify.mfa.setup.submitText"
                validationSchema={verifyTotpSchema}
                initialValues={{ code: '', setAsPreferred: false }}
                PaperProps={{ sx: { minWidth: '700px' } }}
                validateOnMount
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    flexGrow={1}
                    gap={0.5}
                    alignItems="center"
                    mb={1}
                >
                    <AuthenticatorCode />
                    <FormikTextField
                        name="code"
                        label="common.fields.code.label"
                        placeholder="auth.amplify.mfa.totp.placeholder"
                        fullWidth
                        required
                        mb={1}
                    />
                </Box>
                <FormikCheckbox name="setAsPreferred" label="common.setAsPreferred" />
            </FormikDialog>
            <FormikDialog
                title={'views.Account.components.AccountSecurity.dialogs.setupSMS'}
                onClose={handleClose}
                onConfirm={smsSent ? handleSetupConfirm : handleUpdatePhone}
                open={setupType === 'SMS'}
                closeOnConfirm={phoneDisabled}
                backAction={
                    phoneDisabled ? (
                        <Button key="change" onClick={handleChangePhone} variant="outlined">
                            <Locale path="auth.amplify.phone.setup.change" />
                        </Button>
                    ) : undefined
                }
                actions={
                    smsSent
                        ? [
                              <SubmitButton
                                  key="resend"
                                  onClick={handleResendSms}
                                  loading={resendingCode}
                                  variant="text"
                              >
                                  <Locale path="password.resendCode" />
                              </SubmitButton>,
                          ]
                        : []
                }
                confirmText={
                    phoneDisabled
                        ? smsSent
                            ? 'auth.amplify.mfa.setup.submitText'
                            : 'common.confirm'
                        : 'auth.phone.confirmationCode.send'
                }
                validationSchema={verifyPhoneSchema}
                initialValues={{
                    smsSent,
                    phoneDisabled,
                    currentPhone: phone,
                    phone,
                    code: '',
                    setAsPreferred: false,
                    verified: isPhoneVerified,
                }}
                PaperProps={{ sx: { minWidth: '700px' } }}
                validateOnMount
                enableReinitialize
            >
                <Loader loading={resendingCode}>
                    {smsCodeDeliveryDetails && (
                        <Typography mb={2} variant="body2">
                            <Locale
                                path="auth.signIn.confirm.mfa.sms.description"
                                options={smsCodeDeliveryDetails}
                            />
                        </Typography>
                    )}
                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        flexGrow={1}
                        gap={0.5}
                        alignItems="center"
                    >
                        <input type="hidden" name="smsSent" value={smsSent} />
                        <input type="hidden" name="verified" value={isPhoneVerified} />
                        <FormikPhoneNumber
                            name="phone"
                            label="common.fields.phone.label"
                            autoComplete="phone"
                            fullWidth
                            required
                            disabled={phoneDisabled}
                            mb={1}
                            InputProps={{
                                endAdornment:
                                    phoneDisabled && isPhoneVerified ? (
                                        <CheckCircle color="success" />
                                    ) : null,
                            }}
                        />
                        <FormikTextField
                            name="code"
                            label="common.fields.code.label"
                            placeholder="auth.amplify.mfa.sms.placeholder"
                            fullWidth
                            required
                            mb={1}
                            depends={[['smsSent', true, 'verified', false]]}
                        />
                    </Box>
                    <FormikCheckbox
                        name="setAsPreferred"
                        label="common.setAsPreferred"
                        depends={[
                            ['smsSent', true],
                            ['phone', phone, 'phoneDisabled', true],
                        ]}
                    />
                </Loader>
            </FormikDialog>
            <ResponsiveDialog
                title="views.Account.components.AccountSecurity.dialogs.removeAuthentication.title"
                translationOptions={{ removeType }}
                open={!!removeType}
                onClose={handleClose}
                onCancel={handleClose}
                onConfirm={handleRemoveTypeConfirm}
                cancelText="common.no"
                confirmText="common.yes"
                maxWidth="sm"
                fullWidth
            >
                <Box marginBottom={2}>
                    <Locale path="views.Account.components.AccountSecurity.dialogs.removeAuthentication.body" />
                </Box>
                <Box>
                    <Locale path="views.Account.components.AccountSecurity.dialogs.removeAuthentication.footer" />
                </Box>
            </ResponsiveDialog>
        </Portlet>
    );
}

AccountSecurity.propTypes = {
    className: PropTypes.string,
    getMfaOptions: PropTypes.func,
    isLoading: PropTypes.bool,
    isPhoneVerified: PropTypes.bool,
    addMFA: PropTypes.func,
    phone: PropTypes.string,
    hasPhone: PropTypes.bool,
    removeMFA: PropTypes.func,
    mfaOptions: PropTypes.array,
    additionalMfaOptions: PropTypes.array,
    smsCodeDeliveryDetails: PropTypes.object,
    resendPhoneVerificationCode: PropTypes.func,
    updatePhone: PropTypes.func,
};
